import React from 'react';
import { useDisputesOverview } from '../../../hooks/admin/useDisputesOverview';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { AlertCircle, CheckCircle, Clock, TrendingUp } from 'lucide-react';

export function OverviewTiles({ range }) {
  const { data, isLoading, error } = useDisputesOverview(range);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <p className="text-sm text-red-800">
            Failed to load overview metrics. Please try refreshing the page.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { totals, resolution, outcomes } = data;
  const totalOutcomes = (outcomes?.fix || 0) + (outcomes?.partial_refund || 0) + (outcomes?.full_refund || 0);
  
  // Calculate refund rate
  const refundRate = totals?.resolved > 0
    ? (((outcomes?.partial_refund || 0) + (outcomes?.full_refund || 0)) / totals.resolved * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-4">
      {/* Main KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Open Now */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Open Now</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {totals?.open_now || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Opened (Range) */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Opened ({range})</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {totals?.opened || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resolved (Range) */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Resolved ({range})</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {totals?.resolved || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Median Resolution Time */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Median Resolution</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {resolution?.median_hours ? `${resolution.median_hours}h` : '—'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Refund Rate */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Refund Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">{refundRate}%</p>
            <p className="text-sm text-gray-600 mt-1">
              of resolved disputes
            </p>
          </CardContent>
        </Card>

        {/* Outcome Mix */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Outcome Mix ({range})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="px-3 py-1">
                <span className="text-xs font-medium">Fix: {outcomes?.fix || 0}</span>
                {totalOutcomes > 0 && (
                  <span className="ml-1 text-xs text-gray-500">
                    ({((outcomes?.fix || 0) / totalOutcomes * 100).toFixed(0)}%)
                  </span>
                )}
              </Badge>
              
              <Badge variant="outline" className="px-3 py-1">
                <span className="text-xs font-medium">Partial: {outcomes?.partial_refund || 0}</span>
                {totalOutcomes > 0 && (
                  <span className="ml-1 text-xs text-gray-500">
                    ({((outcomes?.partial_refund || 0) / totalOutcomes * 100).toFixed(0)}%)
                  </span>
                )}
              </Badge>
              
              <Badge variant="outline" className="px-3 py-1">
                <span className="text-xs font-medium">Full: {outcomes?.full_refund || 0}</span>
                {totalOutcomes > 0 && (
                  <span className="ml-1 text-xs text-gray-500">
                    ({((outcomes?.full_refund || 0) / totalOutcomes * 100).toFixed(0)}%)
                  </span>
                )}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
